import React from "react";
import { styles } from "../../styles";

interface BasePerpsTabProps {
  connected: boolean;
  loading: boolean;
  privateBalanceUsd: number | null;
  perps: {
    vaultOwner: string;
    setVaultOwner: (v: string) => void;
    accountNft: string;
    setAccountNft: (v: string) => void;
    accountTokenId: string;
    setAccountTokenId: (v: string) => void;
    accountId: string;
    setAccountId: (v: string) => void;
    permissionManager: string;
    setPermissionManager: (v: string) => void;
    ownershipVerifier: string;
    setOwnershipVerifier: (v: string) => void;
    ownershipCommitment: string;
    setOwnershipCommitment: (v: string) => void;
    perpsMarketProxy: string;
    setPerpsMarketProxy: (v: string) => void;
    vaultAddress: string;
    setVaultAddress: (v: string) => void;
    executor: string;
    setExecutor: (v: string) => void;
    permission: string;
    setPermission: (v: string) => void;
    expiry: string;
    setExpiry: (v: string) => void;
    ownershipProof: string;
    setOwnershipProof: (v: string) => void;
    escapeRecipient: string;
    setEscapeRecipient: (v: string) => void;
    traderMarketId: string;
    setTraderMarketId: (v: string) => void;
    traderCollateralAddress: string;
    setTraderCollateralAddress: (v: string) => void;
    traderMarginAmount: string;
    setTraderMarginAmount: (v: string) => void;
    traderOrderSizeDelta: string;
    traderOrderSettlementStrategyId: string;
    setTraderOrderSettlementStrategyId: (v: string) => void;
    traderOrderAcceptablePrice: string;
    traderOrderTrackingCode: string;
    setTraderOrderTrackingCode: (v: string) => void;
    traderOrderReferrer: string;
    setTraderOrderReferrer: (v: string) => void;
    selectedMarket: { label: string };
    availableMarkets: Array<{ key: string; label: string; marketId: string }>;
    selectedMarketKey: string;
    setSelectedMarketKey: (v: string) => void;
    tradeDirection: "long" | "short";
    setTradeDirection: (v: "long" | "short") => void;
    orderPriceUsd: string;
    setOrderPriceUsd: (v: string) => void;
    orderValueUsd: string;
    setOrderValueUsd: (v: string) => void;
    orderLeverage: string;
    setOrderLeverage: (v: string) => void;
    derivedTrade: {
      notionalUsd: number;
      baseSize: number;
      leverage: number;
      requiredMarginUsd: number;
      recommendedMarginInput: string;
    };
    chartEmbedUrl: string;
    hasPerpsAccount: boolean;
    ready: boolean;
    readinessChecking: boolean;
    readinessMessage: string;
    perpsSelectorSupport: {
      openTrade: boolean;
      closeTradeMarket: boolean;
      cancelOpenLimitOrder: boolean;
      updateMargin: boolean;
    } | null;
    metricsLoading: boolean;
    pendingAction: string | null;
    availableMarginUsd: number | null;
    vaultAssetBalanceUsd: number | null;
    vaultWithdrawPreflightMessage: string | null;
    perpsAssetSymbol: string;
    hasActiveTrade: boolean;
    activeTradeSummary: {
      side: "long" | "short";
      collateralUsd: number;
      leverage: number;
      notionalUsd: number;
      baseSize: number | null;
      entryPriceUsd: number | null;
    } | null;
    pendingMarketOrderIds: string[];
    withdrawableMarginUsd: number | null;
    totalCollateralUsd: number | null;
    openPositionSize: number | null;
    unrealizedPnlUsd: number | null;
    refreshReadiness: () => Promise<void>;
    refreshPerpsMetrics: () => Promise<void>;
    handleCreateAccount: () => void;
    handleCommitOrder: () => void;
    handleClosePosition: () => void;
    handleCancelOrder: () => void;
    handleFundVault: () => void;
    handleWithdrawVault: () => void;
    handleDepositMargin: () => void;
    handleWithdrawMargin: () => void;
    applyRecommendedMargin: () => void;
    handleDelegate: () => void;
    handleRevoke: () => void;
    handleEscape: () => void;
  };
  showAdvanced?: boolean;
}

export function BasePerpsTab({
  connected,
  loading,
  privateBalanceUsd,
  perps,
  showAdvanced = false,
}: BasePerpsTabProps) {
  const disabled = !connected || loading || !!perps.pendingAction;
  const createAccountDisabled = disabled || perps.hasPerpsAccount;
  const traderDisabled = disabled || !perps.ready || perps.readinessChecking;
  const requiredMargin = perps.derivedTrade.requiredMarginUsd;
  const orderAvailableMargin =
    perps.availableMarginUsd ?? perps.vaultAssetBalanceUsd;
  const hasMarginSnapshot = orderAvailableMargin != null;
  const insufficientOrderMargin =
    hasMarginSnapshot &&
    Number.isFinite(requiredMargin) &&
    requiredMargin > 0 &&
    (orderAvailableMargin as number) + 1e-9 < requiredMargin;
  const commitDisabled =
    traderDisabled || insufficientOrderMargin || perps.hasActiveTrade;
  const cancelDisabled =
    traderDisabled ||
    perps.pendingMarketOrderIds.length === 0 ||
    perps.perpsSelectorSupport?.cancelOpenLimitOrder === false;
  const closePositionDisabled =
    traderDisabled ||
    !perps.hasActiveTrade ||
    perps.perpsSelectorSupport?.closeTradeMarket === false;
  const secondaryOrderDisabled = perps.hasActiveTrade
    ? closePositionDisabled
    : cancelDisabled;
  const marginActionsDisabled =
    traderDisabled ||
    !perps.hasActiveTrade ||
    perps.perpsSelectorSupport?.updateMargin === false;
  const isPending = (action: string) => perps.pendingAction === action;
  const fmtUsd = (v: number | null) => {
    if (v == null) return "—";
    const abs = Math.abs(v);
    const decimals = abs > 0 && abs < 1 ? 3 : 2;
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };
  const fmtSignedUsd = (v: number | null) => {
    if (v == null) return "—";
    const decimals = Math.abs(v) > 0 && Math.abs(v) < 1 ? 3 : 2;
    const abs = Math.abs(v).toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${v >= 0 ? "+" : "-"}$${abs}`;
  };
  const fmtSize = (v: number | null) => (v == null ? "—" : v.toFixed(6));
  const fmtOrderIds = (ids: string[]) =>
    ids.length === 0 ? "—" : ids.map((id) => `#${id}`).join(", ");
  const marketBaseSymbol = perps.selectedMarket.label.split("/")[0].trim();
  const sideButton = (
    active: boolean,
    positive: boolean,
  ): React.CSSProperties => ({
    ...styles.button,
    width: "100%",
    marginBottom: 0,
    background: active
      ? positive
        ? "#2ad082"
        : "#f87171"
      : "rgba(255,255,255,0.06)",
    color: active ? "#07110b" : "#e8eeff",
    border: active ? "none" : "1px solid rgba(255,255,255,0.1)",
  });

  return (
    <div>
      <div
        style={{
          marginBottom: "14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <div>
          <h3 style={{ marginBottom: "6px", color: "#e8eeff" }}>
            Stealth Perps Exchange
          </h3>
          <p style={{ fontSize: "13px", color: "#b0c4de", margin: 0 }}>
            Avantis private trading interface powered by vault proofs.
          </p>
        </div>
        {perps.hasPerpsAccount ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "999px",
              border: "1px solid rgba(139,226,139,0.35)",
              color: "#8be28b",
              background: "rgba(139,226,139,0.08)",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            Perps Account Active
          </div>
        ) : (
          <button
            className="wallet-cta"
            style={{
              ...styles.button,
              ...styles.primaryButton,
              width: "220px",
              marginBottom: 0,
            }}
            onClick={perps.handleCreateAccount}
            disabled={createAccountDisabled}
          >
            {isPending("createAccount")
              ? "Creating..."
              : "Create Perps Account"}
          </button>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.75fr 1fr",
          gap: "14px",
        }}
      >
        <div
          style={{
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: "12px",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div>
              <div style={{ color: "#e8eeff", fontWeight: 700 }}>
                {perps.selectedMarket.label}
              </div>
              <div style={{ color: "#9cb1cd", fontSize: "12px" }}>
                Market ID {perps.traderMarketId}
              </div>
            </div>
            <select
              style={{ ...styles.input, marginBottom: 0, width: "220px" }}
              value={perps.selectedMarketKey}
              onChange={(e) => perps.setSelectedMarketKey(e.target.value)}
            >
              {perps.availableMarkets.map((market) => (
                <option key={market.key} value={market.key}>
                  {market.label} (#{market.marketId})
                </option>
              ))}
            </select>
          </div>
          <div
            style={{
              height: "420px",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {perps.chartEmbedUrl ? (
              <iframe
                title="Perps Market Chart"
                src={perps.chartEmbedUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  background: "#0b0d1a",
                }}
                loading="lazy"
              />
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                  color: "#b0c4de",
                  fontSize: "13px",
                }}
              >
                Chart embed URL not configured.
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: "12px",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
            <button
              style={sideButton(perps.tradeDirection === "long", true)}
              onClick={() => perps.setTradeDirection("long")}
              disabled={disabled}
            >
              Buy / Long
            </button>
            <button
              style={sideButton(perps.tradeDirection === "short", false)}
              onClick={() => perps.setTradeDirection("short")}
              disabled={disabled}
            >
              Sell / Short
            </button>
          </div>

          <label style={styles.label}>Price (USD)</label>
          <input
            style={styles.input}
            value={perps.orderPriceUsd}
            onChange={(e) => perps.setOrderPriceUsd(e.target.value)}
            placeholder="2078.10"
          />
          <label style={styles.label}>Value (USD)</label>
          <input
            style={styles.input}
            value={perps.orderValueUsd}
            onChange={(e) => perps.setOrderValueUsd(e.target.value)}
            placeholder="200"
          />
          <label style={styles.label}>Leverage (x)</label>
          <input
            style={styles.input}
            value={perps.orderLeverage}
            onChange={(e) => perps.setOrderLeverage(e.target.value)}
            placeholder="5"
          />

          <div
            style={{
              fontSize: "12px",
              color: "#b0c4de",
              marginBottom: "12px",
              lineHeight: 1.5,
            }}
          >
            <div>Private Balance: {fmtUsd(privateBalanceUsd)}</div>
            <div>
              Vault {perps.perpsAssetSymbol} Available:{" "}
              {fmtUsd(perps.vaultAssetBalanceUsd)}{" "}
              {perps.metricsLoading ? "(updating...)" : ""}
            </div>
            <div>
              Withdrawable Margin: {fmtUsd(perps.withdrawableMarginUsd)}
            </div>
            <div>Total Collateral: {fmtUsd(perps.totalCollateralUsd)}</div>
            <div>
              Pending Orders: {fmtOrderIds(perps.pendingMarketOrderIds)}
            </div>
            <div>
              Active Position:{" "}
              {perps.activeTradeSummary
                ? `${perps.activeTradeSummary.side === "long" ? "Long" : "Short"} ${fmtSize(perps.activeTradeSummary.baseSize)} ${marketBaseSymbol}`
                : "—"}
            </div>
            <div>
              Entry Price:{" "}
              {perps.activeTradeSummary?.entryPriceUsd != null
                ? fmtUsd(perps.activeTradeSummary.entryPriceUsd)
                : "—"}
            </div>
            <div>Open Position Size: {fmtSize(perps.openPositionSize)}</div>
            <div>Unrealized PnL: {fmtSignedUsd(perps.unrealizedPnlUsd)}</div>
            <div>
              Notional: $
              {perps.derivedTrade.notionalUsd.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div>Base Size: {perps.derivedTrade.baseSize.toFixed(6)}</div>
            <div>
              Required Margin (est): $
              {perps.derivedTrade.requiredMarginUsd.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>

          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <button
              className="wallet-cta"
              style={{
                ...styles.button,
                ...styles.primaryButton,
                flex: 1,
                marginBottom: 0,
              }}
              onClick={perps.handleCommitOrder}
              disabled={commitDisabled}
            >
              {isPending("commitOrder") ? "Committing..." : "Commit Order"}
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                flex: 1,
                marginBottom: 0,
              }}
              onClick={
                perps.hasActiveTrade
                  ? perps.handleClosePosition
                  : perps.handleCancelOrder
              }
              disabled={secondaryOrderDisabled}
            >
              {perps.hasActiveTrade
                ? isPending("closePosition")
                  ? "Closing..."
                  : "Close Position"
                : isPending("cancelOrder")
                  ? "Cancelling..."
                  : "Cancel Pending"}
            </button>
          </div>
          {perps.hasActiveTrade && (
            <div
              style={{
                marginTop: "-2px",
                marginBottom: "10px",
                color: "#8be28b",
                fontSize: "12px",
              }}
            >
              Position is active. Use Close Position to submit a full Avantis
              market close.
            </div>
          )}
          {perps.perpsSelectorSupport &&
            (!perps.perpsSelectorSupport.cancelOpenLimitOrder ||
              !perps.perpsSelectorSupport.updateMargin) && (
              <div
                style={{
                  marginTop: "-2px",
                  marginBottom: "10px",
                  color: "#f8c08c",
                  fontSize: "12px",
                }}
              >
                Legacy vault selector set: cancel and margin updates are
                unavailable on this vault.
              </div>
            )}
          {insufficientOrderMargin && (
            <div
              style={{
                marginTop: "-2px",
                marginBottom: "10px",
                color: "#f7b2b2",
                fontSize: "12px",
              }}
            >
              Cannot commit order: required margin is {fmtUsd(requiredMargin)},
              vault has {fmtUsd(orderAvailableMargin)} {perps.perpsAssetSymbol}.
            </div>
          )}
          <label style={styles.label}>Amount ({perps.perpsAssetSymbol})</label>
          <input
            style={styles.input}
            value={perps.traderMarginAmount}
            onChange={(e) => perps.setTraderMarginAmount(e.target.value)}
            placeholder="18"
          />
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <button
              className="wallet-cta"
              style={{
                ...styles.button,
                ...styles.primaryButton,
                flex: 1,
                marginBottom: 0,
              }}
              onClick={perps.handleFundVault}
              disabled={traderDisabled}
            >
              {isPending("fundVault") ? "Funding..." : "Fund Vault"}
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                flex: 1,
                marginBottom: 0,
              }}
              onClick={perps.handleWithdrawVault}
              disabled={traderDisabled}
            >
              {isPending("withdrawVault") ? "Withdrawing..." : "Withdraw Vault"}
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                flex: 1,
                marginBottom: 0,
              }}
              onClick={perps.handleDepositMargin}
              disabled={marginActionsDisabled}
            >
              {isPending("depositMargin") ? "Adding..." : "Add Margin"}
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                flex: 1,
                marginBottom: 0,
              }}
              onClick={perps.handleWithdrawMargin}
              disabled={marginActionsDisabled}
            >
              {isPending("withdrawMargin") ? "Reducing..." : "Reduce Margin"}
            </button>
          </div>
          {perps.vaultWithdrawPreflightMessage && (
            <div
              style={{
                marginTop: "-2px",
                marginBottom: "10px",
                color: "#f7b2b2",
                fontSize: "12px",
              }}
            >
              {perps.vaultWithdrawPreflightMessage}
            </div>
          )}
          {!perps.hasActiveTrade && (
            <div
              style={{
                marginTop: "-2px",
                marginBottom: "10px",
                color: "#f8c08c",
                fontSize: "12px",
              }}
            >
              Open trade uses initial collateral from vault. Add/Reduce Margin
              unlocks after a position is active. Withdraw Vault sends idle
              vault {perps.perpsAssetSymbol} back to private balance.
            </div>
          )}

          <div
            style={{
              padding: "10px",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.03)",
              fontSize: "12px",
              color: "#b0c4de",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <strong style={{ color: "#e8eeff" }}>Vault Readiness</strong>
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  width: "90px",
                  padding: "6px 10px",
                  marginBottom: 0,
                }}
                disabled={disabled || perps.readinessChecking}
                onClick={() => {
                  void perps.refreshReadiness();
                  void perps.refreshPerpsMetrics();
                }}
              >
                {perps.readinessChecking ? "Check..." : "Refresh"}
              </button>
            </div>
            <div style={{ color: perps.ready ? "#8be28b" : "#f8c08c" }}>
              {perps.readinessMessage}
            </div>
            <div style={{ marginTop: "8px" }}>
              Vault: {perps.vaultAddress || "Not set"}
            </div>
            <div>Account: {perps.accountId || "0"}</div>
          </div>
        </div>
      </div>

      {showAdvanced && (
        <div
          style={{
            marginBottom: "18px",
            padding: "12px",
            borderRadius: "10px",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <label style={styles.label}>Vault Owner</label>
          <input
            style={styles.input}
            value={perps.vaultOwner}
            onChange={(e) => perps.setVaultOwner(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Account NFT</label>
          <input
            style={styles.input}
            value={perps.accountNft}
            onChange={(e) => perps.setAccountNft(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Account Token ID</label>
          <input
            style={styles.input}
            value={perps.accountTokenId}
            onChange={(e) => perps.setAccountTokenId(e.target.value)}
            placeholder="42"
          />

          <label style={styles.label}>Account ID (uint128)</label>
          <input
            style={styles.input}
            value={perps.accountId}
            onChange={(e) => perps.setAccountId(e.target.value)}
            placeholder="4242"
          />

          <label style={styles.label}>Permission Manager</label>
          <input
            style={styles.input}
            value={perps.permissionManager}
            onChange={(e) => perps.setPermissionManager(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Ownership Verifier</label>
          <input
            style={styles.input}
            value={perps.ownershipVerifier}
            onChange={(e) => perps.setOwnershipVerifier(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Ownership Commitment (bytes32)</label>
          <input
            style={styles.input}
            value={perps.ownershipCommitment}
            onChange={(e) => perps.setOwnershipCommitment(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Perps Market Proxy (optional)</label>
          <input
            style={styles.input}
            value={perps.perpsMarketProxy}
            onChange={(e) => perps.setPerpsMarketProxy(e.target.value)}
            placeholder="0x..."
          />

          <button
            className="wallet-cta"
            style={{ ...styles.button, ...styles.primaryButton, width: "100%" }}
            onClick={perps.handleCreateAccount}
            disabled={createAccountDisabled}
          >
            {isPending("createAccount")
              ? "Creating..."
              : perps.hasPerpsAccount
                ? "Perps Account Already Created"
                : "Deploy Vault (Sponsored)"}
          </button>
        </div>
      )}

      {showAdvanced && (
        <div
          style={{
            marginBottom: "18px",
            padding: "12px",
            borderRadius: "10px",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <label style={styles.label}>Executor</label>
          <input
            style={styles.input}
            value={perps.executor}
            onChange={(e) => perps.setExecutor(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Permission (bytes32)</label>
          <input
            style={styles.input}
            value={perps.permission}
            onChange={(e) => perps.setPermission(e.target.value)}
            placeholder="0x..."
          />

          <label style={styles.label}>Expiry (unix seconds)</label>
          <input
            style={styles.input}
            value={perps.expiry}
            onChange={(e) => perps.setExpiry(e.target.value)}
            placeholder="1735689600"
          />

          <label style={styles.label}>Ownership Proof (hex bytes)</label>
          <textarea
            style={{
              ...styles.input,
              minHeight: "84px",
              resize: "vertical" as const,
            }}
            value={perps.ownershipProof}
            onChange={(e) => perps.setOwnershipProof(e.target.value)}
            placeholder="0x..."
          />

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="wallet-cta"
              style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}
              onClick={perps.handleDelegate}
              disabled={disabled}
            >
              {isPending("delegate") ? "Delegating..." : "Delegate (Sponsored)"}
            </button>
            <button
              className="wallet-cta"
              style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}
              onClick={perps.handleRevoke}
              disabled={disabled}
            >
              {isPending("revoke") ? "Revoking..." : "Revoke (Sponsored)"}
            </button>
          </div>
        </div>
      )}

      {showAdvanced && (
        <div
          style={{
            padding: "12px",
            borderRadius: "10px",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <label style={styles.label}>Escape Recipient</label>
          <input
            style={styles.input}
            value={perps.escapeRecipient}
            onChange={(e) => perps.setEscapeRecipient(e.target.value)}
            placeholder="0x..."
          />
          <button
            style={{
              ...styles.button,
              background: "#ffb4b4",
              color: "#1a0909",
              width: "100%",
            }}
            onClick={perps.handleEscape}
            disabled={disabled}
          >
            {isPending("escape")
              ? "Escaping..."
              : "Escape Transfer (Sponsored)"}
          </button>
        </div>
      )}
    </div>
  );
}
